import React from 'react'
import { useFormikContext } from 'formik'
import { Autocomplete } from '@carsys/form'
import { IRevenueGroupAdministration, VatType } from '@cbgms/api'
import { Grid, ListItem, Paper, Popper, PopperProps, Typography } from '@mui/material'
import { useEnumTranslation } from '@cbgms/base/enums/use-enum-translation'
import { useTranslation } from 'react-i18next'
import { WorkorderInvoiceType } from '@cbgms/api/modules/enums/workorder-invoice-type'

interface IProps {
  tabIndex: number
}

const InvoiceLedgers: React.FC<IProps> = ({ tabIndex }) => {
  const { t } = useTranslation()
  const { te } = useEnumTranslation()
  const {
    values: { Administrations }
  } = useFormikContext<IRevenueGroupAdministration>()

  const autocompleteLedgerDefaultProps = {
    freeSolo: true,
    autoSelect: true,
    options:
      Administrations[tabIndex].LedgerAccounts?.map(item => {
        return { text: item.Code, value: item.Code, label: `${item.Code} ${item.Description}` }
      }) || [],
    renderOption: (props: any, option: any) => (
      <ListItem key={props.key}>{option?.label || option?.text || option?.value || option}</ListItem>
    ),
    PopperComponent: (props: PopperProps) => {
      return (
        <Popper {...props} placement='bottom-start' style={{ width: 'fit-content', minWidth: props?.style?.width, maxWidth: '80vh' }} />
      )
    }
  }

  const getInvoiceLedgerTitle = (type: WorkorderInvoiceType) =>
    type === WorkorderInvoiceType.Warranty ? t('workorder.warranty_sale') : t('workorder.internal_sale')

  const getInvoiceLedgers = () => {
    const invoiceLedgers = Administrations[tabIndex].Ledgers.map((ledger, index) => {
      // We add arrayIndex to have the correct order of fetched items in the array.
      const ledgerWithArrayIndex = { ...ledger, arrayIndex: index }
      return ledgerWithArrayIndex
    })
      .filter(ledger => ledger.InvoiceType === WorkorderInvoiceType.Internal || ledger.InvoiceType === WorkorderInvoiceType.Warranty)
      .map(ledger => ({ ...ledger, VatType: VatType.None, LedgerName: getInvoiceLedgerTitle(ledger.InvoiceType) }))

    return invoiceLedgers
  }

  return (
    <Paper sx={{ p: 2 }}>
      <Grid container spacing={2}>
        {getInvoiceLedgers().map(ledger => (
          <Grid key={ledger.arrayIndex} item xs={6}>
            <Grid item xs={12}>
              <Typography variant='h6'>{ledger.LedgerName}</Typography>
            </Grid>
            <Grid container spacing={1} p={2}>
              <Grid container item spacing={1}>
                <Grid item xs={12}>
                  <Typography variant='subtitle1'>{te('EnumVatType', ledger.VatType)}</Typography>
                </Grid>
                <Grid container item spacing={2} xs={12}>
                  <Grid item xs={6}>
                    <Autocomplete
                      name={`Administrations[${tabIndex}].Ledgers[${ledger.arrayIndex}].Revenue`}
                      label={t('revenue_group.revenue')}
                      {...autocompleteLedgerDefaultProps}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Autocomplete
                      name={`Administrations.${tabIndex}.Ledgers.${ledger.arrayIndex}.Discount`}
                      label={t('revenue_group.discount')}
                      {...autocompleteLedgerDefaultProps}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        ))}
      </Grid>
    </Paper>
  )
}

export default InvoiceLedgers
